<template>
  <div
    ref="elRef"
    class="IsoChatOverlay"
  >
    <svg
      class="IsoChatOverlay__svg"
    >
      <path
        ref="pathRef"
        class="IsoChatOverlay__path"
        :d="path"
      />
      <circle
        ref="startDotRef"
        class="IsoChatOverlay__dot--block IsoChatOverlay__dot"
        :cx="points[0]?.x"
        :cy="points[0]?.y"
      />
      <circle
        ref="startDotOutlineRef"
        class="IsoChatOverlay__dotOutline"
        :cx="points[0]?.x"
        :cy="points[0]?.y"
      />
      <circle
        ref="endDotRef"
        class="IsoChatOverlay__dot--chat IsoChatOverlay__dot"
        :cx="points[2]?.x"
        :cy="points[2]?.y"
      />
    </svg>

    <div
      class="IsoChatOverlay__chatContainer"
      :style="{
        left: `${points[2]?.x + 16}px`,
        top: `${points[2]?.y - 18}px`,
      }"
    >
      <slot />
    </div>
  </div>
</template>

<script lang="ts" setup>
import useThreeElement from "./useThreeElement";
import delay from "./delay";
import { easeInCubicString, easeInOutCubicString, easeOutCubicString } from "@shared/lib/animation/easing";
import getPointFromVector from "./getPointFromVector";
import { Vector2, Vector3 } from "three";
import { computed, ref, toRefs } from "vue";

const props = defineProps<{
  vector: Vector3,
}>();

defineExpose({
  animateIn,
  animateOut,
});

const {
  camera,
  canvas,
  viewportOffset,
} = useThreeElement();

const {
  vector,
} = toRefs(props);

const elRef = ref<HTMLElement>();
const startDotRef = ref<SVGCircleElement>();
const startDotOutlineRef = ref<SVGCircleElement>();
const endDotRef = ref<SVGCircleElement>();
const pathRef = ref<SVGPathElement>();

const points = computed((): Array<Vector2> => {
  return [
    new Vector3(0, 1, 0),
    new Vector3(0, 2.25, 0),
    new Vector3(0, 2.25, -1.25),
  ].map((_vector) => {
    return getPointFromVector(
      vector.value.clone().add(viewportOffset.value)
        .add(_vector),
      camera,
      canvas,
    );
  });
});

const path = computed((): string => {
  return points.value.reduce<Array<string>>((acc, point, i) => {
    if (i === 0) {
      acc.push(`M${point.x},${point.y - 8}`);
    } else {
      acc.push(`L${point.x},${point.y}`);
    }

    return acc;
  }, []).join(" ");
});

async function animateIn (): Promise<void> {
  if (
    !startDotRef.value
    || !startDotOutlineRef.value
    || !pathRef.value
    || !endDotRef.value
  ) { return; }

  await Promise.all([
    animateDotIn(startDotRef.value),
    animateDotIn(startDotOutlineRef.value),
    animatePathIn(pathRef.value),
    animateDotIn(endDotRef.value, {
      delay: 600,
    }),
  ]);
}

async function animateOut (): Promise<void> {
  if (
    !startDotRef.value
    || !startDotOutlineRef.value
    || !pathRef.value
    || !endDotRef.value
  ) { return Promise.reject(new Error("IsoChatOverlay elements missing")); }

  animateDotOut(endDotRef.value);
  await delay(50);
  animatePathOut(pathRef.value);
  await delay(150);
  animateDotOut(startDotRef.value);
  animateDotOut(startDotOutlineRef.value);
}

function animateDotIn (dot: SVGCircleElement, {
  delay,
} = {
  delay: 0,
}): Promise<Animation> {
  return dot.animate([
    {
      transform: "scale(0)",
    },
    {
      transform: "scale(1)",
    },
  ], {
    duration: 350,
    delay,
    easing: easeInOutCubicString,
    fill: "forwards",
  }).finished;
}

function animateDotOut (dot: SVGCircleElement, {
  delay,
} = {
  delay: 0,
}): Promise<Animation> {
  return dot.animate([
    {
      transform: "scale(1)",
    },
    {
      transform: "scale(0)",
    },
  ], {
    duration: 350,
    delay,
    easing: easeInOutCubicString,
    fill: "forwards",
  }).finished;
}

function animatePathIn (path: SVGPathElement): Promise<Animation> {
  const pathLength = path.getTotalLength();
  path.style.strokeDasharray = `${pathLength}px ${pathLength}px`;

  return path.animate([
    {
      strokeDashoffset: `${pathLength}px`,
    },
    {
      strokeDashoffset: "0px",
    },
  ], {
    duration: 750,
    easing: easeInCubicString,
    fill: "forwards",
  }).finished;
}

function animatePathOut (path: SVGPathElement): Promise<Animation> {
  const pathLength = path.getTotalLength();
  path.style.strokeDasharray = `${pathLength}px ${pathLength}px`;

  return path.animate([
    {
      strokeDashoffset: "0px",
    },
    {
      strokeDashoffset: `${pathLength}px`,
    },
  ], {
    duration: 600,
    easing: easeOutCubicString,
    fill: "forwards",
  }).finished;
}
</script>

<style lang="scss" scoped>
.IsoChatOverlay {
  position: absolute;
  inset: 0;
}

.IsoChatOverlay__svg {
  position: absolute;
  width: 100%;
  height: 100%;
}

.IsoChatOverlay__path {
  stroke: var(--colorSwissBlack);
  stroke-width: 2px;
  stroke-dasharray: 999px 999px;
  stroke-dashoffset: 999px;
  fill: none;
}

.IsoChatOverlay__dot {
  transform: scale(0);
  transform-origin: center;
  transform-box: fill-box;

  r: 4px;
  fill: var(--colorSwissBlack);
}

.IsoChatOverlay__dotOutline {
  transform: scale(0);
  transform-origin: center;
  transform-box: fill-box;

  r: 8px;
  fill: none;
  stroke: var(--colorSwissBlack);
}

.IsoChatOverlay__chatContainer {
  position: absolute;
  isolation: isolate;

  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}
</style>
